import { TrendingDown, TrendingUp } from "lucide-react";

import { cn } from "@/lib/utils";

import { formatEuro } from "../gulaConstants";
import type { CostSheet, ProfitResult, RecurringActuals } from "./profit";

const Line = ({
  label,
  amount,
  strong,
  muted,
}: {
  label: string;
  amount: number;
  strong?: boolean;
  muted?: boolean;
}) => (
  <div
    className={cn(
      "flex justify-between gap-3 py-0.5 text-sm",
      strong && "border-t pt-1.5 font-semibold",
      muted && "text-muted-foreground",
    )}
  >
    <span>{label}</span>
    <span className="tabular-nums">{formatEuro(amount)}</span>
  </div>
);

const minus = (value: number) => (value ? -value : 0);

/** Step-by-step calculation of what one sale has left so far. */
export const ProfitBreakdown = ({
  sheet,
  actuals,
  result,
}: {
  sheet: CostSheet;
  actuals: RecurringActuals;
  result: ProfitResult;
}) => {
  const hasRecurring =
    Number(sheet.recurring_gross_month) > 0 || actuals.collectedGross > 0;
  const positive = result.profitToDate >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const optional = (label: string, value: number) =>
    value ? <Line label={label} amount={-value} /> : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border bg-muted/40 p-4">
        <div className="text-sm text-muted-foreground">
          Beneficio con este cliente hasta hoy
        </div>
        <div
          className={cn(
            "mt-1 flex items-center gap-2 text-3xl font-semibold tabular-nums",
            !positive && "text-destructive",
          )}
        >
          <Icon className="h-6 w-6" aria-hidden />
          {formatEuro(result.profitToDate)}
        </div>
        <div className="mt-1 text-sm text-muted-foreground">
          {result.marginPct != null
            ? `Margen ${result.marginPct.toLocaleString("es-ES")} % sobre lo cobrado sin IVA`
            : "Todavía no hay ingresos"}
          {hasRecurring
            ? ` · cada mes suma ${formatEuro(result.recurringProfitMonth)}`
            : ""}
        </div>
        {result.paybackMonths ? (
          <div className="mt-1 text-sm">
            Recupera lo invertido en {result.paybackMonths}{" "}
            {result.paybackMonths === 1 ? "mes" : "meses"} más de cuota.
          </div>
        ) : null}
      </div>

      <div>
        <h4 className="mb-1 text-sm font-semibold">Venta del equipo</h4>
        <Line
          label="Precio cobrado (IVA incl.)"
          amount={Number(sheet.sale_gross)}
        />
        <Line
          label={`IVA (${sheet.vat_rate} %)`}
          amount={minus(result.saleVat)}
          muted
        />
        {optional("Comisión del cobro", result.saleFee)}
        {optional("Comisión del setter", result.commission)}
        {optional("Equipo BLUA", Number(sheet.equipment_cost))}
        {optional("Grifo 3 vías", Number(sheet.faucet_cost))}
        {optional("Instalación", Number(sheet.installer_cost))}
        {optional(
          "Instalación extra del grifo",
          Number(sheet.faucet_install_cost),
        )}
        {optional("Envío", Number(sheet.shipping_cost))}
        {optional(
          sheet.other_costs_note || "Otros costes",
          Number(sheet.other_costs),
        )}
        <Line label="Beneficio de la venta" amount={result.saleProfit} strong />
      </div>

      {hasRecurring ? (
        <div>
          <h4 className="mb-1 text-sm font-semibold">
            Cuotas (mantenimiento y alquiler)
          </h4>
          <Line
            label={`Cobrado por Stripe (${actuals.paymentsCount} ${actuals.paymentsCount === 1 ? "cobro" : "cobros"}, IVA incl.)`}
            amount={actuals.collectedGross}
          />
          <Line
            label="IVA"
            amount={minus(
              actuals.collectedGross - result.recurringCollectedNet,
            )}
            muted
          />
          {optional("Comisiones de Stripe", result.recurringFeesToDate)}
          {optional(
            `Filtros y membrana (${actuals.monthsActive} ${actuals.monthsActive === 1 ? "mes" : "meses"})`,
            result.recurringCostsToDate,
          )}
          <Line
            label="Beneficio de las cuotas"
            amount={result.recurringProfitToDate}
            strong
          />
        </div>
      ) : null}
    </div>
  );
};
